import { useEffect, useRef, useState } from "react";
import { Phone, Mail, Globe, MessageCircle, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SERVICE_OPTIONS = [
  "Daily Cab Service",
  "Fleet Rental",
  "Airport Transfer",
  "Sedan",
  "6-Seater",
  "Toyota Innova Crysta",
  "Tempo Traveller",
  "Coach",
  "Premium Vehicle",
  "Other",
];

const initialForm = {
  company_name: "",
  contact_person: "",
  phone: "",
  email: "",
  employee_count: "",
  service_required: "Daily Cab Service",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const formRef = useRef(null);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  // Listen for "prefill" events dispatched by the Fleet section's Get Quote buttons
  useEffect(() => {
    const handler = (event) => {
      const service = event?.detail?.service;
      if (service && SERVICE_OPTIONS.includes(service)) {
        setForm((f) => ({ ...f, service_required: service }));
        // briefly highlight the dropdown
        const select = formRef.current?.querySelector('[data-testid="form-service-required"]');
        if (select) {
          select.classList.add("ring-2", "ring-[#F5A623]");
          setTimeout(() => select.classList.remove("ring-2", "ring-[#F5A623]"), 1800);
        }
      }
    };
    window.addEventListener("rydey:prefill", handler);
    return () => window.removeEventListener("rydey:prefill", handler);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      setSuccess(true);
      setForm(initialForm);
      toast.success("Quote request received. Our team will reach out within 24 hours.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Could not submit. Please try again or call us directly.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section-pad bg-[#0D1B40] relative overflow-hidden" data-testid="contact-section">
      <div
        className="absolute -left-40 -bottom-40 w-[500px] h-[500px] rounded-full opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(circle, #F5A623 0%, transparent 60%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-5 gap-10">
          {/* Left: copy + contact info */}
          <div className="lg:col-span-2 reveal">
            <div className="eyebrow mb-4">Contact Us</div>
            <h2 className="text-white font-extrabold text-[34px] md:text-[44px] tracking-tight leading-tight">
              Get a Custom Quote for <span className="text-[#F5A623]">Your Company.</span>
            </h2>
            <p className="mt-5 text-white/75 text-[16px] leading-relaxed">
              Tell us about your team's commute needs. Our team will respond within 24
              hours with a tailored proposal — including pricing, vehicle mix, and a
              dedicated dashboard demo.
            </p>

            <div className="mt-9 space-y-4">
              <ContactRow
                icon={<Phone size={18} />}
                label="Phone"
                value="+91 7012998898"
                href="tel:+917012998898"
                testId="contact-phone"
              />
              <ContactRow
                icon={<Mail size={18} />}
                label="Email"
                value="support@rydey.in"
                href="mailto:support@rydey.in"
                testId="contact-email"
              />
              <ContactRow
                icon={<Globe size={18} />}
                label="Website"
                value="www.rydey.in"
                href="https://www.rydey.in"
                testId="contact-website"
              />
              <a
                href="https://wa.me/917012998898?text=Hi%20Rydey%2C%20I%27d%20like%20a%20corporate%20cab%20quote."
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-cta"
                className="mt-4 inline-flex items-center gap-2.5 px-5 py-3 rounded-lg bg-[#25D366] text-white font-semibold text-[14px] hover:bg-[#1eb858] transition-all"
              >
                <MessageCircle size={18} /> Chat on WhatsApp
              </a>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-3 reveal">
            <form
              ref={formRef}
              onSubmit={onSubmit}
              data-testid="quote-form"
              className="bg-white rounded-2xl p-7 md:p-9 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)]"
            >
              {success && (
                <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200 flex items-start gap-3">
                  <CheckCircle2 className="text-green-600 mt-0.5" size={20} />
                  <div>
                    <div className="font-semibold text-green-800 text-[14px]">Request received!</div>
                    <div className="text-green-700 text-[13px]">
                      Our team will get back to you within 24 hours.
                    </div>
                  </div>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Company Name" required>
                  <input
                    required
                    name="company_name"
                    value={form.company_name}
                    onChange={onChange}
                    className="field-input"
                    placeholder="Acme Technologies Pvt Ltd"
                    data-testid="form-company-name"
                  />
                </Field>
                <Field label="Contact Person" required>
                  <input
                    required
                    name="contact_person"
                    value={form.contact_person}
                    onChange={onChange}
                    className="field-input"
                    placeholder="Full name"
                    data-testid="form-contact-person"
                  />
                </Field>
                <Field label="Phone Number" required>
                  <input
                    required
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    className="field-input"
                    placeholder="+91 ..........."
                    data-testid="form-phone"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    className="field-input"
                    placeholder="you@company.com"
                    data-testid="form-email"
                  />
                </Field>
                <Field label="Number of Employees" required>
                  <select
                    required
                    name="employee_count"
                    value={form.employee_count}
                    onChange={onChange}
                    className="field-input"
                    data-testid="form-employee-count"
                  >
                    <option value="">Select range</option>
                    <option value="1-25">1 – 25</option>
                    <option value="26-100">26 – 100</option>
                    <option value="101-500">101 – 500</option>
                    <option value="501-1000">501 – 1000</option>
                    <option value="1000+">1000+</option>
                  </select>
                </Field>
                <Field label="Services Required" required>
                  <select
                    required
                    name="service_required"
                    value={form.service_required}
                    onChange={onChange}
                    className="field-input transition-all"
                    data-testid="form-service-required"
                  >
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="mt-4">
                <Field label="Message">
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={4}
                    className="field-input resize-none"
                    placeholder="Tell us about routes, shift timings, or any specific requirements..."
                    data-testid="form-message"
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="form-submit"
                className="mt-6 w-full sm:w-auto btn-primary justify-center disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Submitting...
                  </>
                ) : (
                  <>
                    Request a Quote <ArrowRight size={18} />
                  </>
                )}
              </button>

              <p className="mt-4 text-[12.5px] text-slate-500">
                We respect your privacy. Your details are only used to respond to this
                inquiry.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="field-label">
        {label} {required && <span className="text-[#F5A623]">*</span>}
      </span>
      {children}
    </label>
  );
}

function ContactRow({ icon, label, value, href, testId }) {
  return (
    <a
      href={href}
      data-testid={testId}
      className="group flex items-center gap-4 p-3.5 rounded-lg hover:bg-white/5 transition-colors"
    >
      <div className="w-11 h-11 rounded-lg bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center group-hover:bg-[#F5A623] group-hover:text-[#0D1B40] transition-colors">
        {icon}
      </div>
      <div>
        <div className="text-[11px] tracking-[0.18em] text-white/55 font-semibold uppercase">
          {label}
        </div>
        <div className="text-white font-semibold text-[15px]">{value}</div>
      </div>
    </a>
  );
}
